// svg-to-react-pdf.tsx
import {
  Circle,
  Ellipse,
  G,
  Line,
  Path,
  Polygon,
  Polyline,
  Rect,
  Svg,
  Text,
  Tspan,
} from '@react-pdf/renderer';
import React from 'react';

const PT_PER_IN = 72;
const PT_PER_MM = PT_PER_IN / 25.4;
const PT_PER_CM = PT_PER_IN / 2.54;

function parseCssLength(value?: string | number, basisForEm?: number): number | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'number') {
    return value;
  }

  const m = String(value)
    .trim()
    .match(/^(-?\d*\.?\d+)([a-z%]*)$/i);
  if (!m) {
    return undefined;
  }

  const n = parseFloat(m[1]);
  const unit = (m[2] || '').toLowerCase();

  if (unit === '' || unit === 'px') {
    return n;
  } // treat px == user units
  if (unit === 'pt') {
    return n;
  }
  if (unit === 'in') {
    return n * PT_PER_IN;
  }
  if (unit === 'mm') {
    return n * PT_PER_MM;
  }
  if (unit === 'cm') {
    return n * PT_PER_CM;
  }
  if (unit === 'em' || unit === 'rem') {
    return (basisForEm ?? 12) * n;
  } // fallback

  // Unknown units -> best effort: return the numeric part
  return n;
}

function safeGetComputedStyle(el: Element): Partial<CSSStyleDeclaration> | null {
  try {
    if (typeof window === 'undefined' || !('getComputedStyle' in window)) {
      return null;
    }
    // JSDOM bug/quirk: some SVG elements have no .style, causing internals to blow up
    if (!(el as any).style) {
      return null;
    }
    return window.getComputedStyle(el);
  } catch {
    return null;
  }
}

function normalizeDasharray(v?: string | number[]): number[] | undefined {
  if (v == null) {
    return undefined;
  }
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === 'number' ? x : parseCssLength(x)!)).filter(Number.isFinite);
  }
  const parts = String(v).replace(/,/g, ' ').trim().split(/\s+/);
  const nums = parts
    .map((p) => parseCssLength(p))
    .filter((n): n is number => Number.isFinite(n as number));
  return nums.length ? nums : undefined;
}

// convert any `translate(10px, 2mm) scale(1)` -> numbers without units
function sanitizeTransform(t?: string): string | undefined {
  if (!t) {
    return t;
  }
  return t.replace(/(-?\d*\.?\d+)(px|pt|mm|cm|in)/gi, (_, num: string, unit: string) => {
    const n = parseCssLength(num + unit);
    return n != null ? String(n) : num;
  });
}

const STYLE_MAP: Record<string, string> = {
  // svg attr -> react prop (camelCase)
  'dominant-baseline': 'dominantBaseline',
  'alignment-baseline': 'alignmentBaseline',
  'stroke-width': 'strokeWidth',
  'stroke-dasharray': 'strokeDasharray',
  'stroke-linecap': 'strokeLinecap',
  'stroke-linejoin': 'strokeLinejoin',
  'stroke-miterlimit': 'strokeMiterlimit',
  'fill-opacity': 'fillOpacity',
  'stroke-opacity': 'strokeOpacity',
  'font-family': 'fontFamily',
  'font-size': 'fontSize',
  'font-weight': 'fontWeight',
  'text-anchor': 'textAnchor',
};

const directAttrs = [
  'id',
  'transform',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-miterlimit',
  'fill-opacity',
  'stroke-opacity',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
  'alignment-baseline',
  // positional/size
  'x',
  'y',
  'dx',
  'dy',
  'x1',
  'y1',
  'x2',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'width',
  'height',
];

const computedAttrs = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
];

function isHidden(el: Element): boolean {
  const displayAttr = readAttr(el, 'display');
  if (displayAttr?.toLowerCase() === 'none') {
    return true;
  }

  const visAttr = readAttr(el, 'visibility');
  if (visAttr?.toLowerCase() === 'hidden') {
    return true;
  }

  const style = (el as HTMLElement).style as CSSStyleDeclaration | undefined;
  if (style) {
    if (style.display === 'none') {
      return true;
    }
    if (style.visibility === 'hidden') {
      return true;
    }
    if (style.opacity === '0') {
      return true;
    }
  }

  const cs = safeGetComputedStyle(el);
  if (cs && (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0')) {
    return true;
  }
  return false;
}

// Normalize Unicode minus to ASCII hyphen
function normalizeMinus(s: string) {
  return s.replace(/\u2212/g, '-');
}

// Normalize all numeric-ish props to numbers (points) and clean dasharray/transform
function coerceSvgNumericProps(props: Record<string, any>, tag?: string) {
  const FONT_SIZE =
    typeof props.fontSize === 'number'
      ? props.fontSize
      : parseCssLength(props.fontSize as string) ?? 12;

  const numericKeys = [
    'x',
    'y',
    'dx',
    'dy',
    'x1',
    'y1',
    'x2',
    'y2',
    'cx',
    'cy',
    'r',
    'rx',
    'ry',
    'width',
    'height',
    'strokeWidth',
    'opacity',
    'fillOpacity',
    'strokeOpacity',
    'fontSize',
    'strokeMiterlimit',
  ];

  for (const k of numericKeys) {
    if (props[k] != null) {
      const coerced = parseCssLength(props[k] as string, FONT_SIZE);
      if (coerced != null) {
        props[k] = coerced;
      }
    }
  }
  if (props.strokeDasharray != null) {
    props.strokeDasharray = normalizeDasharray(props.strokeDasharray as string);
  }
  if (props.transform) {
    props.transform = sanitizeTransform(props.transform as string);
  }
}

const NUMERIC_ATTRS = new Set([
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'width',
  'height',
  'strokeWidth',
  'opacity',
  'fontSize',
]);

function readAttr(el: Element, name: string): string | undefined {
  const v = el.getAttribute(name);
  return v == null || v === '' ? undefined : v;
}

function readStyle(el: Element, cssName: string): string | undefined {
  const style = (el as HTMLElement).style as CSSStyleDeclaration | undefined;
  const camel = cssName.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  const inline = style?.[camel as keyof CSSStyleDeclaration] as unknown as string | undefined;
  if (inline) {
    return inline;
  }

  const cs = safeGetComputedStyle(el);
  if (cs) {
    const val = (cs as any)[camel] as string | undefined;
    if (val && !['initial', 'none', 'normal', 'rgba(0, 0, 0, 0)'].includes(val)) {
      return val;
    }
  }
  return undefined;
}

function assignIfDefined<T extends Record<string, any>>(obj: T, key: string, v?: string) {
  if (v != null) {
    (obj as any)[key] = NUMERIC_ATTRS.has(key) && !Number.isNaN(Number(v)) ? Number(v) : v;
  }
}

function normalizeFontFamily(ff?: string): string | undefined {
  if (!ff) {
    return undefined;
  }

  // Take first family in the list and strip quotes
  const first = ff
    .split(',')[0]
    .trim()
    .replace(/^['"]|['"]$/g, '');
  const lower = first.toLowerCase();

  if (['serif', 'ui-serif', 'times new roman', 'georgia'].includes(lower)) {
    return 'Times-Roman';
  }
  if (['monospace', 'ui-monospace', 'consolas', 'menlo', 'courier new'].includes(lower)) {
    return 'Courier';
  }

  return 'Helvetica';
}

function gatherCommonProps(el: Element) {
  const tag = el.tagName.toLowerCase(); // <-- infer here

  const props: Record<string, any> = {};

  for (const a of directAttrs) {
    const v = readAttr(el, a);
    if (v !== undefined) {
      assignIfDefined(props, STYLE_MAP[a] ?? a, v);
    }
  }

  for (const cssName of computedAttrs) {
    const v = readStyle(el, cssName);
    if (v !== undefined && props[STYLE_MAP[cssName] ?? cssName] === undefined) {
      assignIfDefined(props, STYLE_MAP[cssName] ?? cssName, v);
    }
  }

  // Fonts
  props.fontFamily = normalizeFontFamily(props.fontFamily as string);

  // Sanitize transform units before coercion (optional but nice)
  if (props.transform) {
    props.transform = sanitizeTransform(props.transform as string);
  }

  // Coerce numeric-ish props ("1px" -> 1, "4px 2px" -> "4 2", etc.)
  coerceSvgNumericProps(props, tag);

  // Normalize weight for base fonts
  if (typeof props.fontWeight === 'string') {
    const w = props.fontWeight.toLowerCase();
    if (!['normal', 'bold'].includes(w)) {
      delete props.fontWeight;
    }
  }

  return props;
}

function convertNode(el: Element, key: string): React.ReactNode | null {
  if (isHidden(el)) {
    return null;
  }
  const tag = el.tagName.toLowerCase();
  const props = { key, ...gatherCommonProps(el) } as any;

  switch (tag) {
    case 'g': {
      const children = Array.from(el.children)
        .map((c, i) => convertNode(c, `${key}.${i}`))
        .filter(Boolean);
      return <G {...props}>{children}</G>;
    }

    case 'path': {
      const d = readAttr(el, 'd');
      if (!d) {
        return null;
      }
      return <Path {...props} d={d} fill={props.fill ?? 'none'} />;
    }

    case 'line': {
      return (
        <Line
          {...props}
          x1={props.x1 ?? 0}
          x2={props.x2 ?? 0}
          y1={props.y1 ?? 0}
          y2={props.y2 ?? 0}
        />
      );
    }

    case 'rect': {
      return (
        <Rect
          {...props}
          height={props.height ?? 0}
          rx={props.rx}
          ry={props.ry}
          width={props.width ?? 0}
          x={props.x ?? 0}
          y={props.y ?? 0}
        />
      );
    }

    case 'circle': {
      return <Circle {...props} cx={props.cx ?? 0} cy={props.cy ?? 0} r={props.r ?? 0} />;
    }

    case 'ellipse': {
      return (
        <Ellipse
          {...props}
          cx={props.cx ?? 0}
          cy={props.cy ?? 0}
          rx={props.rx ?? 0}
          ry={props.ry ?? 0}
        />
      );
    }

    case 'polyline': {
      const points = readAttr(el, 'points');
      if (!points) {
        return null;
      }
      return <Polyline {...props} points={points} />;
    }

    case 'polygon': {
      const points = readAttr(el, 'points');
      if (!points) {
        return null;
      }
      return <Polygon {...props} points={points} />;
    }

    case 'text': {
      const textProps = gatherCommonProps(el);
      textProps.fontFamily = normalizeFontFamily(textProps.fontFamily as string);
      coerceSvgNumericProps(textProps, 'text');

      const baseX = textProps.x ?? 0;
      const baseY = textProps.y ?? 0;
      const dx = textProps.dx ?? 0;
      const dy = textProps.dy ?? 0;
      delete textProps.dx;
      delete textProps.dy;

      const childNodes = Array.from(el.childNodes);
      const tspans = childNodes
        .map((n, i) => {
          if (n.nodeType === Node.TEXT_NODE) {
            const content = normalizeMinus(n.textContent ?? '');
            return content.trim() ? <Tspan key={`${key}.t${i}`}>{content}</Tspan> : null;
          }
          if (
            n.nodeType === Node.ELEMENT_NODE &&
            (n as Element).tagName.toLowerCase() === 'tspan'
          ) {
            const ts = n as Element;
            const tsProps: Record<string, any> = {};
            assignIfDefined(tsProps, 'x', readAttr(ts, 'x'));
            assignIfDefined(tsProps, 'y', readAttr(ts, 'y'));
            assignIfDefined(tsProps, 'dx', readAttr(ts, 'dx'));
            assignIfDefined(tsProps, 'dy', readAttr(ts, 'dy'));
            coerceSvgNumericProps(tsProps, 'tspan');
            const textContent = normalizeMinus(ts.textContent ?? '');
            return (
              <Tspan key={`${key}.ts${i}`} {...tsProps}>
                {textContent}
              </Tspan>
            );
          }
          return null;
        })
        .filter(Boolean);

      return (
        <Text {...(textProps as any)} x={baseX + dx} y={baseY + dy}>
          {tspans}
        </Text>
      );
    }

    // Unsupported/ignored:
    case 'defs':
    case 'clipPath':
    case 'mask':
    case 'pattern':
    case 'marker':
    case 'image':
    case 'use':
    case 'foreignObject':
      return null;

    default:
      // Try to convert children if any (e.g., unknown container)
      if ((el as HTMLElement).children?.length) {
        const children = Array.from(el.children)
          .map((c, i) => convertNode(c, `${key}.${i}`))
          .filter(Boolean);
        return <G {...props}>{children}</G>;
      }
      return null;
  }
}

export function buildGraphSvg(svg: SVGSVGElement): JSX.Element {
  // Size + viewBox from source
  // Prefer viewBox; fall back to bounding box
  let viewBox = svg.getAttribute('viewBox');
  if (!viewBox) {
    const w = Number((svg.getAttribute('width') ?? svg.clientWidth) || 1200);
    const h = Number((svg.getAttribute('height') ?? svg.clientHeight) || 400);
    viewBox = `0 0 ${w} ${h}`;
  }

  const children = Array.from(svg.children)
    .map((c, i) => convertNode(c, `r.${i}`))
    .filter(Boolean);

  return (
    <Svg style={{ width: '100%', height: '100%' }} viewBox={viewBox}>
      {children}
    </Svg>
  );
}
